import { Order } from 'models/Order';
import { PackageProviderName } from '../../../data/ppoints/constants';
import httpApi from '../../../hooks/api/httpApi';
import { ShopifyOrderNoteAttributeName } from '../../../models/ppoints/ShopifyOrderNoteAttributeName';
import { OrderStatusFilters } from '../models/OrderStatusFilters';
import { ShippingType } from '../models/ShippingType';
import { ShopifyOrder } from '../models/ShopifyOrder';
import { VariantImage } from '../models/VariantImage';
import { OrdersTableColumn } from '../utils/orderUtils';

const getNoteAttributeValue = (order: ShopifyOrder, attributeName: ShopifyOrderNoteAttributeName) => {
    return order?.note_attributes?.find((attribute) => attribute.name === attributeName)?.value;
};

const getShippingType = (order: ShopifyOrder): ShippingType | null => {
    const noteAttrValue = getNoteAttributeValue(order, ShopifyOrderNoteAttributeName.shippingType);
    switch (noteAttrValue) {
        case 'mpl_pp':
        case 'mpl_cs':
            return ShippingType.MPL_CS;

        case 'mpl_package_point':
            return ShippingType.MPL_DELIVERY;
        case 'foxpost':
            return ShippingType.FOXPOST;
        case 'mpl_mol':
            return ShippingType.MPL_MOL;
        case 'mpl_mediamarkt':
            return ShippingType.MPL_MEDIAMARKT;
        case 'mpl_coop':
            return ShippingType.MPL_COOP;
        case 'mpl_office':
            return ShippingType.MPL_OFFICE;
        case 'gls':
            return ShippingType.GLS;
        case 'packeta':
            return ShippingType.PACKETA;
        case 'foxpost_hd':
            return ShippingType.FOXPOST_HD;
        case 'foxpost_apm':
            return ShippingType.FOXPOST_APM;
        case 'foxpost_packeta_pp':
            return ShippingType.FOXPOST_PACKETA_PP;
        case 'foxpost_packeta_apm':
            return ShippingType.FOXPOST_PACKETA_APM;
        case 'dpd':
            return ShippingType.DPD;
        case 'sprinter':
            return ShippingType.SPRINTER;
        case 'fancourier_office':
            return ShippingType.FANCOURIER_OFFICE;
        case 'fancourier_fanbox':
            return ShippingType.FANCOURIER_FANBOX;
        case 'fancourier_paypoint':
            return ShippingType.FANCOURIER_PAYPOINT;
        case 'sameday_hd':
            return ShippingType.SAMEDAY_HD;
        case 'sameday_ooh':
            return ShippingType.SAMEDAY_OOH;
        case 'sameday_locker':
            return ShippingType.SAMEDAY_LOCKER;
        default:
            return null;
    }
};

const getOrder = async (ecomId: number, orderId: number) => {
    let res = await httpApi.get<{ order: ShopifyOrder }>(`/api/ecom/${ecomId}/orders/v1/${orderId}`);
    let data = res.getData();
    return data.order;
};

const getOrderLineItemImages = async (ecomId: number, variantIds: string[]) => {
    let res = await httpApi.get<VariantImage[]>(`/api/ecom/${ecomId}/products/variants/images`, {
        variant_id: variantIds.join(','),
    });
    let data = res.getData();
    return data;
};

const getCustomerName = (order: Order) => {
    if (!order.customerInfo.lastName || !order.customerInfo.firstName) {
        return 'Protected customer data';
    } else {
        return `${order.customerInfo.firstName} ${order.customerInfo.lastName}`;
    }
};

const sort = (orders, index, direction) => {
    try {
        switch (index) {
            /*case OrdersTableColumn.order_number:
            case OrdersTableColumn.total:
            case OrdersTableColumn.items:
                return [...orders].sort((rowA, rowB) => {
                    const amountA = rowA[OrdersTableColumn[index]];
                    const amountB = rowB[OrdersTableColumn[index]];
                    return direction === 'descending' ? amountB - amountA : amountA - amountB;
                });*/
            case OrdersTableColumn.customer:
                return [...orders].sort((rowA, rowB) => {
                    const customerA = rowA[OrdersTableColumn[index]];
                    const customerB = rowB[OrdersTableColumn[index]];
                    const nameA = `${customerA.first_name} ${customerA.last_name}`;
                    const nameB = `${customerB.first_name} ${customerB.last_name}`;
                    return direction === 'descending' ? nameB.localeCompare(nameA) : nameA.localeCompare(nameB);
                });
            case OrdersTableColumn.created_at:
                return [...orders].sort((rowA, rowB) => {
                    let dateA = rowA[OrdersTableColumn[index]];
                    let dateB = rowB[OrdersTableColumn[index]];
                    dateA = new Date(dateA).getTime();
                    dateB = new Date(dateB).getTime();
                    return direction === 'descending' ? dateB - dateA : dateA - dateB;
                });

            /**
             * if column is string
             */
            // case etc:
            //     const textA = rowA[OrdersTableColumn[index]];
            //     const textB = rowB[OrdersTableColumn[index]];
            //     return direction === 'descending' ? textB.localeCompare(textA) : textA.localeCompare(textB);

            default:
                return orders;
        }
    } catch (e) {
        return orders;
    }
};

const isSelectableOrders = (statusFilters: OrderStatusFilters, selectedProviderFilter: PackageProviderName) => {
    return (
        (selectedProviderFilter === PackageProviderName.MPL_PACKAGE_POINT &&
            statusFilters[PackageProviderName.MPL_PACKAGE_POINT] !== null) ||
        (selectedProviderFilter === PackageProviderName.FOXPOST &&
            statusFilters[PackageProviderName.FOXPOST] !== null) ||
        (selectedProviderFilter === PackageProviderName.GLS && statusFilters[PackageProviderName.GLS] !== null)
    );
};

export default {
    getNoteAttributeValue,
    getOrder,
    getCustomerName,
    sort,
    getShippingType,
    isSelectableOrders,
    getOrderLineItemImages,
};
